import React, { FC, PropsWithChildren } from 'react';
import styles from './StepLayout.scss';
import { Button, ButtonProps, Text, TextButton } from '@wix/design-system';
import { ChevronLeft } from '@wix/wix-ui-icons-common';
import { useTranslation } from '@wix/wix-i18n-config';
import { StepParams, useOrchestratorNavigation, useOrchestratorParams } from '@wix/gg-components/orchestrator';

type Props = {
  title?: string;
  description?: string;
  onNext?: () => void;
  onBack?: () => void;
  hideActions?: boolean;
  disclaimer?: string;
  nextButtonProps?: Pick<ButtonProps, 'disabled'> & {
    label: string;
  };
};

export const StepLayout: FC<PropsWithChildren<Props>> = ({
  children,
  title,
  description,
  onNext,
  onBack,
  nextButtonProps,
  hideActions,
  disclaimer,
}) => {
  const { t } = useTranslation();
  const { next, back, canGoBack } = useOrchestratorNavigation();
  const stepParams = useOrchestratorParams<StepParams>();

  const handlePrimaryButtonClick = () => {
    onNext?.();
    next();
  };

  const handleBackButtonClick = () => {
    onBack?.();
    back();
  };

  return (
    <div className={styles.stepLayout}>
      <div className={styles.stepLayoutContent}>
        <div className={styles.stepLayoutContentHeader}>
          {title && <p className={styles.stepLayoutTitle}>{title}</p>}
          {description && <p className={styles.stepLayoutDescription}>{description}</p>}
        </div>
        <div className={styles.stepLayoutBody}>{children}</div>
      </div>
      {disclaimer && (
        <div className={styles.stepLayoutDisclaimer}>
          <Text secondary light size="tiny">
            {disclaimer}
          </Text>
        </div>
      )}
      {!hideActions && (
        <div className={styles.stepLayoutFooter}>
          <div className={styles.stepLayoutFooterContent}>
            {canGoBack && !stepParams?.disableBack ? (
              <TextButton skin="dark" prefixIcon={<ChevronLeft />} onClick={handleBackButtonClick}>
                {t('actions-bar.button.back')}
              </TextButton>
            ) : (
              <div />
            )}
            <Button {...nextButtonProps} skin="dark" onClick={handlePrimaryButtonClick}>
              {nextButtonProps?.label}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
